/**
 * SIL-2358: bench test plan per device class and the tighten-only partner overlay. Pure functions, no DB.
 * Design: docs/superpowers/specs/2026-10-29-bench-r2-passport-design.md 8.4. DB twin: migration 1216
 * (BEFORE INSERT triggers on test_plan_overlay_limit / test_plan_overlay_function enforce the same rules; this module is the API
 * first line and MUST agree with them: tests/sql/bench_test_plan_overlay_sil_2358.sql and __tests__/test-plan.test.ts).
 *
 * The REC derivation (rec-derivation.ts) consumes `PlanFunction` and `RequiredClass`; this module produces exactly those types
 * (imported, not redefined). Limits are decimal STRINGS: ledger payloads are integer-only (1212), so measurements and limits
 * travel as strings and are compared exactly here (no float arithmetic).
 */
import { z } from 'zod';
import { type FunctionVerdict, type PlanFunction, type RequiredClass, type TestPlanDeviceClass } from './rec-derivation.js';
import type { BenchDeviceKind } from './contract.js';

/**
 * Discovery device kind -> plan/REC class. chassis has no plan class (the unit, not a component under test). The `drive` class
 * covers every data drive: the `nvme` kind and the `drive` kind (SATA/SAS, SIL-2367).
 */
export const DEVICE_KIND_TO_CLASS: Readonly<Record<BenchDeviceKind, TestPlanDeviceClass | null>> = {
  chassis: null,
  gpu: 'gpu',
  nvme: 'drive',
  drive: 'drive',
  nic: 'nic',
  dimm: 'dimm',
  cpu: 'cpu',
  psu: 'psu',
};
export function deviceKindToClass(kind: BenchDeviceKind): TestPlanDeviceClass | null {
  return DEVICE_KIND_TO_CLASS[kind];
}

/** bench_plan_area (1187) -> plan/REC class. inventory and chassis carry no plan class. */
export const AREA_TO_CLASS: Readonly<Record<string, TestPlanDeviceClass | null>> = {
  inventory: null,
  gpu: 'gpu',
  ram: 'dimm',
  cpu: 'cpu',
  storage: 'drive',
  chassis: null,
};
export function areaToDeviceClass(area: string): TestPlanDeviceClass | null {
  return AREA_TO_CLASS[area] ?? null;
}

export type LimitDirection = 'max' | 'min';
export type LimitKind = 'limit' | 'duration';
export type UnitRequirement = 'required' | 'optional_if_present' | 'none';

/** One SiliconX base limit of a plan function. `limit` is a non-negative decimal string. */
export interface PlanLimit {
  measurement: string;
  kind: LimitKind;
  direction: LimitDirection;
  unit: string;
  limit: string;
  /**
   * SIL-2391: the measurement must be reported; a missing value makes the function result `incomplete`, never a pass.
   * Absent (older fixtures, pre-1240 callers) means false.
   */
  required?: boolean;
}

/** A plan function. Extends the REC input type, so a plan function IS a `PlanFunction` (functionId, key). */
export interface TestPlanFunction extends PlanFunction {
  description: string;
  /** REC F4. Every key function is also a hardware function. */
  hardware: boolean;
  mandatory: boolean;
  /** On the plan's optional list: an overlay may add it. */
  optional: boolean;
  /** Existing bench_step_kind (migrations 1187, 1192) that runs this function, if any. */
  stepKind: string | null;
  limits: readonly PlanLimit[];
}

export interface TestPlanVersion {
  planId: string;
  version: number;
  deviceClass: TestPlanDeviceClass;
  unitRequirement: UnitRequirement;
  functions: readonly TestPlanFunction[];
}

export interface OverlayLimit {
  functionId: string;
  measurement: string;
  unit: string;
  /** Decimal string. */
  limit: string;
}

export type OverlayFunctionAction = 'make_mandatory' | 'add_optional';

export interface OverlayFunction {
  functionId: string;
  action: OverlayFunctionAction;
}

/** One overlay version. It pins ONE plan version. */
export interface TestPlanOverlay {
  overlayId: string;
  version: number;
  planId: string;
  planVersion: number;
  limits: readonly OverlayLimit[];
  functions: readonly OverlayFunction[];
}

// ---------------------------------------------------------------------------
// Exact decimal compare (non-negative decimals only)

const DECIMAL_RE = /^(?:0|[1-9][0-9]{0,17})(?:\.[0-9]{1,9})?$/;

export function isNonNegativeDecimal(v: unknown): v is string {
  return typeof v === 'string' && DECIMAL_RE.test(v);
}

/** -1, 0, 1. Both inputs must pass `isNonNegativeDecimal`. */
export function compareDecimal(a: string, b: string): -1 | 0 | 1 {
  const [ai = '0', af = ''] = a.split('.');
  const [bi = '0', bf = ''] = b.split('.');
  const scale = Math.max(af.length, bf.length);
  const x = BigInt(ai + af.padEnd(scale, '0'));
  const y = BigInt(bi + bf.padEnd(scale, '0'));
  return x < y ? -1 : x > y ? 1 : 0;
}

// ---------------------------------------------------------------------------
// Tighten-only validation

export type OverlayProblemCode =
  | 'plan_mismatch'
  | 'duplicate'
  | 'unknown_limit'
  | 'bad_value'
  | 'unit_mismatch'
  | 'loosens'
  | 'unknown_function'
  | 'already_mandatory'
  | 'not_optional'
  | 'both_actions';

export interface OverlayProblem {
  code: OverlayProblemCode;
  functionId: string;
  measurement?: string;
  message: string;
}

/**
 * The API-side tighten-only check. An overlay may only: lower a max limit, raise a min limit (a duration is a min limit),
 * make a non-mandatory function mandatory, add a function from the plan's optional list. Units are compared as strings and
 * never converted. Returns every problem; an empty array means the overlay is a pure tightening.
 */
export function validateOverlay(plan: TestPlanVersion, overlay: TestPlanOverlay): OverlayProblem[] {
  const problems: OverlayProblem[] = [];
  if (overlay.planId !== plan.planId || overlay.planVersion !== plan.version) {
    problems.push({ code: 'plan_mismatch', functionId: '', message: 'overlay is pinned to a different plan version' });
    return problems;
  }
  const byId = new Map(plan.functions.map((f) => [f.functionId, f]));

  const seen = new Set<string>();
  for (const l of overlay.limits) {
    const key = `${l.functionId}\u0000${l.measurement}`;
    if (seen.has(key)) {
      problems.push({ code: 'duplicate', functionId: l.functionId, measurement: l.measurement, message: 'limit given twice' });
      continue;
    }
    seen.add(key);
    const base = byId.get(l.functionId)?.limits.find((b) => b.measurement === l.measurement);
    if (!base) {
      problems.push({ code: 'unknown_limit', functionId: l.functionId, measurement: l.measurement, message: 'the plan has no base limit for it' });
      continue;
    }
    if (!isNonNegativeDecimal(l.limit)) {
      problems.push({ code: 'bad_value', functionId: l.functionId, measurement: l.measurement, message: 'limit must be a non-negative decimal' });
      continue;
    }
    if (l.unit !== base.unit) {
      problems.push({ code: 'unit_mismatch', functionId: l.functionId, measurement: l.measurement, message: `unit ${l.unit} differs from ${base.unit}; units are never converted` });
      continue;
    }
    const cmp = compareDecimal(l.limit, base.limit);
    if ((base.direction === 'max' && cmp > 0) || (base.direction === 'min' && cmp < 0)) {
      problems.push({ code: 'loosens', functionId: l.functionId, measurement: l.measurement, message: `would loosen the ${base.direction} limit ${base.limit}` });
    }
  }

  const fnSeen = new Set<string>();
  for (const f of overlay.functions) {
    if (fnSeen.has(f.functionId)) {
      problems.push({ code: 'both_actions', functionId: f.functionId, message: 'function listed twice' });
      continue;
    }
    fnSeen.add(f.functionId);
    const base = byId.get(f.functionId);
    if (!base) {
      problems.push({ code: 'unknown_function', functionId: f.functionId, message: 'not a function of the plan version' });
    } else if (f.action === 'make_mandatory' && base.mandatory) {
      problems.push({ code: 'already_mandatory', functionId: f.functionId, message: 'already mandatory in the plan' });
    } else if (f.action === 'add_optional' && !base.optional) {
      problems.push({ code: 'not_optional', functionId: f.functionId, message: 'not on the plan optional list' });
    }
  }
  return problems;
}

// ---------------------------------------------------------------------------
// Effective plan

export interface EffectiveLimit {
  measurement: string;
  kind: LimitKind;
  direction: LimitDirection;
  unit: string;
  baseLimit: string;
  /** What the run is judged against: the overlay value when present, else the base. */
  effectiveLimit: string;
  /** SIL-2391: from the plan limit; an overlay cannot change it. */
  required: boolean;
}

export interface EffectiveFunction {
  functionId: string;
  key: boolean;
  hardware: boolean;
  /** Part of the effective plan: mandatory in the plan, or made mandatory / added by the overlay. */
  inPlan: boolean;
  stepKind: string | null;
  limits: readonly EffectiveLimit[];
}

export function effectivePlan(plan: TestPlanVersion, overlay?: TestPlanOverlay | null): EffectiveFunction[] {
  if (overlay && (overlay.planId !== plan.planId || overlay.planVersion !== plan.version)) {
    throw new Error('overlay is pinned to a different plan version');
  }
  const added = new Set((overlay?.functions ?? []).map((f) => f.functionId));
  const ovLimit = new Map((overlay?.limits ?? []).map((l) => [`${l.functionId}\u0000${l.measurement}`, l.limit]));
  return plan.functions.map((f) => ({
    functionId: f.functionId,
    key: f.key,
    hardware: f.hardware,
    inPlan: f.mandatory || added.has(f.functionId),
    stepKind: f.stepKind,
    limits: f.limits.map((l) => ({
      measurement: l.measurement,
      kind: l.kind,
      direction: l.direction,
      unit: l.unit,
      baseLimit: l.limit,
      effectiveLimit: ovLimit.get(`${f.functionId}\u0000${l.measurement}`) ?? l.limit,
      required: l.required ?? false,
    })),
  }));
}

// ---------------------------------------------------------------------------
// REC derivation inputs (exactly the types rec-derivation.ts consumes)

/** Functions the effective plan requires for a component. Key flag drives F3; every listed function drives F4. */
export function toRecPlanFunctions(effective: readonly EffectiveFunction[]): PlanFunction[] {
  return effective.filter((f) => f.inPlan).map((f) => ({ functionId: f.functionId, key: f.key }));
}

/** The plan's contribution to `UnitRecInput.requiredClasses` (D14). `none` contributes nothing. */
export function toRequiredClass(plan: TestPlanVersion): RequiredClass | null {
  if (plan.unitRequirement === 'none') return null;
  return { deviceClass: plan.deviceClass, optional: plan.unitRequirement === 'optional_if_present' };
}

// ---------------------------------------------------------------------------
// Existing plans (1187, 1188, 1192): step kinds map to named functions

/**
 * Map the step kinds of an existing org/template plan to the named functions that run them. A kind with no function (wipe,
 * firmware_record, inventory_record) maps to null: it is not a test function. An existing plan therefore runs exactly as
 * before; the mapping only decides which `bench.function_result` entries MAY be produced, and a plan without any result entries
 * simply derives no REC functional category (components report missing functions, never an error).
 */
export function mapStepKindsToFunctions(
  plans: readonly TestPlanVersion[],
  stepKinds: readonly string[],
): { stepKind: string; deviceClass: TestPlanDeviceClass | null; functionId: string | null }[] {
  return stepKinds.map((stepKind) => {
    for (const p of plans) {
      const f = p.functions.find((x) => x.stepKind === stepKind);
      if (f) return { stepKind, deviceClass: p.deviceClass, functionId: f.functionId };
    }
    return { stepKind, deviceClass: null, functionId: null };
  });
}

// ---------------------------------------------------------------------------
// bench.function_result ledger payload (schema_version 1). Keys are snake_case; ids end in _id (ledger payload guard);
// numbers are decimal strings (integer-only ledger).

export const FUNCTION_RESULT_PAYLOAD_VERSION = 1;
export const FUNCTION_VERDICTS = ['pass', 'fail', 'not_run', 'incomplete'] as const satisfies readonly FunctionVerdict[];

const decimal = z.string().regex(DECIMAL_RE);
const stamp = z.string().datetime({ offset: true });

export const functionResultPayloadSchema = z
  .object({
    plan_id: z.string().uuid(),
    plan_version: z.number().int().min(1),
    overlay_id: z.string().uuid().nullable(),
    overlay_version: z.number().int().min(1).nullable(),
    function_id: z.string().regex(/^[a-z][a-z0-9_]{1,47}$/),
    effective_limits: z
      .array(
        z
          .object({
            measurement: z.string().min(1).max(48),
            direction: z.enum(['max', 'min']),
            unit: z.string().min(1).max(24),
            limit: decimal,
            /** SIL-2391: absent on entries written before the flag existed, read as false. */
            required: z.boolean().optional(),
          })
          .strict(),
      )
      .max(32),
    measurements: z.array(z.object({ measurement: z.string().min(1).max(48), unit: z.string().min(1).max(24), value: decimal }).strict()).max(64),
    verdict: z.enum(FUNCTION_VERDICTS),
    /** From stage-reasons.json for not_run / incomplete; null for pass and fail. */
    reason_code: z.string().min(1).max(64).nullable(),
    tool: z.string().min(1).max(64),
    tool_version: z.string().min(1).max(64),
    /** Server-stamped receipt times, labelled apart from the agent's own times. */
    received_started_at: stamp,
    received_ended_at: stamp,
    agent_started_at: stamp.nullable(),
    agent_ended_at: stamp.nullable(),
  })
  .strict()
  .refine((p) => (p.overlay_id === null) === (p.overlay_version === null), { message: 'overlay_id and overlay_version are set together' })
  .refine((p) => (p.verdict === 'pass' || p.verdict === 'fail') === (p.reason_code === null), {
    message: 'reason_code is required for not_run and incomplete and absent for pass and fail',
  });
export type FunctionResultPayload = z.infer<typeof functionResultPayloadSchema>;

/** Build the effective_limits array an entry stores. */
export function effectiveLimitsForEntry(f: EffectiveFunction): FunctionResultPayload['effective_limits'] {
  return f.limits.map((l) => ({ measurement: l.measurement, direction: l.direction, unit: l.unit, limit: l.effectiveLimit, required: l.required }));
}

/**
 * Pass/fail of one measurement against its effective limit. Equal to the limit passes (limits are inclusive both ways).
 * Returns null when the value is not a valid decimal.
 */
export function measurementWithinLimit(l: Pick<EffectiveLimit, 'direction' | 'effectiveLimit'>, value: string): boolean | null {
  if (!isNonNegativeDecimal(value) || !isNonNegativeDecimal(l.effectiveLimit)) return null;
  const c = compareDecimal(value, l.effectiveLimit);
  return l.direction === 'max' ? c <= 0 : c >= 0;
}

// ---------------------------------------------------------------------------
// SIL-2391: function verdict from measurements and effective limits

/** reason_code of an `incomplete` result whose required measurement was not reported. */
export const REQUIRED_MEASUREMENT_MISSING = 'required_measurement_missing';
/** reason_code of an `incomplete` result whose reported value is not a valid decimal (never judged as a pass). */
export const MEASUREMENT_INVALID = 'measurement_invalid';

export interface ReportedMeasurement {
  measurement: string;
  /** Decimal string. */
  value: string;
}

export interface LimitEvaluation {
  verdict: 'pass' | 'fail' | 'incomplete';
  /** Set for `incomplete` only. */
  reasonCode: typeof REQUIRED_MEASUREMENT_MISSING | typeof MEASUREMENT_INVALID | null;
  /** Measurements over their limit. */
  failed: string[];
  /** Required limits with no reported measurement. */
  missingRequired: string[];
}

/**
 * Judge one function's reported measurements against its effective limits.
 *  - a measurement outside its limit: `fail` (a definite fail wins over anything missing);
 *  - a required limit with no measurement, or a value that is not a valid decimal: `incomplete`, never `pass`;
 *  - a non-required limit with no measurement is not judged (unchanged pre-2391 behavior);
 *  - otherwise `pass` (0 against max 0 passes, 1 fails).
 * A measurement named twice is judged on every copy (the worst one decides).
 */
export function evaluateFunctionLimits(limits: readonly EffectiveLimit[], measurements: readonly ReportedMeasurement[]): LimitEvaluation {
  const failed: string[] = [];
  const missingRequired: string[] = [];
  let invalid = false;
  for (const l of limits) {
    const reported = measurements.filter((m) => m.measurement === l.measurement);
    if (reported.length === 0) {
      if (l.required) missingRequired.push(l.measurement);
      continue;
    }
    for (const m of reported) {
      const within = measurementWithinLimit(l, m.value);
      if (within === null) invalid = true;
      else if (!within && !failed.includes(l.measurement)) failed.push(l.measurement);
    }
  }
  if (failed.length > 0) return { verdict: 'fail', reasonCode: null, failed, missingRequired };
  if (missingRequired.length > 0) return { verdict: 'incomplete', reasonCode: REQUIRED_MEASUREMENT_MISSING, failed, missingRequired };
  if (invalid) return { verdict: 'incomplete', reasonCode: MEASUREMENT_INVALID, failed, missingRequired };
  return { verdict: 'pass', reasonCode: null, failed, missingRequired };
}
